import { Minus, Plus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Campul „câți elevi au participat" de la Exercitii cu clasa.
 *
 * 🔴 Sta pe ACEEASI pagina cu incarcarea pozelor (sesizarea ECOTIC din 2026-10-22). Pana atunci
 * era un ecran separat, DUPA „Trimite raspuns": raspunsul ajungea la admin cu `participants = 1`
 * (implicitul din `LessonAnswer`), iar cine iesea inainte de al doilea ecran il lasa asa.
 *
 * ⚠️ Porneste GOL, nu de la 1 sau 2: o valoare implicita care trece de validare e exact un numar pe
 * care profesorul nu l-a scris. Eroarea apare doar dupa ce omul a atins campul sau a incercat sa
 * trimita — nu la incarcarea paginii.
 */

export const ID_CAMP_PARTICIPANTI = 'camp-participanti';
export const MINIM_PARTICIPANTI = 2;

export function esteNumarParticipantiValid(valoare: number | null): valoare is number {
    return valoare !== null && Number.isInteger(valoare) && valoare >= MINIM_PARTICIPANTI;
}

export interface CampParticipantiProps {
    value: number | null;
    onChange: (value: number | null) => void;
    disabled?: boolean;
    /** S-a incercat trimiterea cu campul invalid — eroarea se arata chiar daca n-a fost atins. */
    incercatTrimiterea?: boolean;
}

export function CampParticipanti({ value, onChange, disabled, incercatTrimiterea = false }: CampParticipantiProps) {
    const [atins, setAtins] = useState(false);
    const arataEroarea = (atins || incercatTrimiterea) && !esteNumarParticipantiValid(value);
    const idAjutor = `${ID_CAMP_PARTICIPANTI}-ajutor`;
    const idEroare = `${ID_CAMP_PARTICIPANTI}-eroare`;

    const schimba = (urmator: number | null) => {
        setAtins(true);
        onChange(urmator);
    };

    return (
        // Continutul e centrat in chenar (cerut de owner, 2026-10-22).
        <div className="space-y-3 text-center">
            <label htmlFor={ID_CAMP_PARTICIPANTI} className="block font-semibold text-foreground">
                Câți elevi au participat? <span aria-hidden="true" className="text-error">*</span>
                <span className="sr-only">(obligatoriu)</span>
            </label>

            <div className="flex items-center justify-center gap-3">
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => schimba(Math.max(MINIM_PARTICIPANTI, (value ?? MINIM_PARTICIPANTI + 1) - 1))}
                    disabled={disabled || value === null || value <= MINIM_PARTICIPANTI}
                    aria-label="Scade numărul de participanți"
                    aria-controls={ID_CAMP_PARTICIPANTI}
                    className="h-12 w-12 shrink-0 rounded-full"
                >
                    <Minus className="h-5 w-5" aria-hidden="true" />
                </Button>

                <Input
                    id={ID_CAMP_PARTICIPANTI}
                    type="number"
                    inputMode="numeric"
                    min={MINIM_PARTICIPANTI}
                    step={1}
                    required
                    value={value ?? ''}
                    onChange={(e) => {
                        const n = Number.parseInt(e.target.value, 10);
                        onChange(Number.isNaN(n) ? null : n);
                    }}
                    onBlur={() => setAtins(true)}
                    disabled={disabled}
                    // Fara placeholder: in fontul mare si bold al campului, „ex. 25" se citea ca o
                    // valoare deja completata (vazut la 360px) — exact numarul pe care nu l-a scris nimeni.
                    autoComplete="off"
                    aria-describedby={arataEroarea ? `${idAjutor} ${idEroare}` : idAjutor}
                    aria-invalid={arataEroarea ? true : undefined}
                    className={`h-12 w-28 text-center text-xl font-bold tabular-nums ${arataEroarea ? 'border-error' : ''}`}
                />

                <Button
                    type="button"
                    variant="outline"
                    onClick={() => schimba(value === null ? MINIM_PARTICIPANTI : value + 1)}
                    disabled={disabled}
                    aria-label="Crește numărul de participanți"
                    aria-controls={ID_CAMP_PARTICIPANTI}
                    className="h-12 w-12 shrink-0 rounded-full"
                >
                    <Plus className="h-5 w-5" aria-hidden="true" />
                </Button>
            </div>

            <p id={idAjutor} className="text-sm text-muted-foreground">
                Numărul de elevi din clasă care au lucrat fișa. Minim {MINIM_PARTICIPANTI}.
            </p>

            {arataEroarea && (
                <p id={idEroare} role="alert" className="text-sm font-medium text-error">
                    {value === null
                        ? 'Scrie câți elevi au participat, ca să poți trimite.'
                        : `Trebuie să fie minim ${MINIM_PARTICIPANTI} participanți.`}
                </p>
            )}
        </div>
    );
}
